"use client"

import { useEffect, useRef, useState } from "react"

import { ProjetoDialog } from "./ProjetoDialog"
import { useProjetoAtivo } from "@/lib/projeto-planejamento/context"
import type { ProjetoPlanejamento } from "@/lib/projeto-planejamento/types"

function formatPeriodo(p: ProjetoPlanejamento): string {
  if (p.anoInicio === p.anoFim) return String(p.anoInicio)
  return `${p.anoInicio}-${p.anoFim}`
}

export function ProjetoSelector() {
  const { projetoAtivo, projetos, setProjetoAtivo } = useProjetoAtivo()
  const [open, setOpen] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const ativos = projetos.filter((p) => p.ativo)

  useEffect(() => {
    if (!open) return
    function onClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [open])

  return (
    <>
      <div ref={containerRef} className="relative">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Projeto ativo"
          className="flex items-center gap-2 rounded border border-markv-primary-soft-border bg-markv-primary-soft px-3 py-1.5 text-sm text-markv-primary-soft-fg hover:bg-markv-primary-soft"
        >
          <span
            aria-hidden="true"
            className="text-xs uppercase tracking-wide text-markv-primary-accent"
          >
            Projeto
          </span>
          <span className="max-w-[200px] truncate font-medium">
            {projetoAtivo ? projetoAtivo.nome : "Nenhum projeto"}
          </span>
          {projetoAtivo ? (
            <span className="rounded bg-markv-primary-soft px-1.5 py-0.5 text-xs text-markv-primary-soft-fg">
              {formatPeriodo(projetoAtivo)}
            </span>
          ) : null}
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            className="h-3.5 w-3.5"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {open ? (
          <div
            role="menu"
            className="absolute right-0 z-20 mt-1 w-72 rounded border border-markv-surface-200 bg-markv-surface-50 py-1 shadow-md"
          >
            {ativos.length === 0 ? (
              <p className="px-3 py-2 text-xs text-markv-text-400">
                Nenhum projeto ativo.
              </p>
            ) : (
              ativos.map((projeto) => {
                const selected = projeto.id === projetoAtivo?.id
                return (
                  <button
                    key={projeto.id}
                    type="button"
                    role="menuitemradio"
                    aria-checked={selected}
                    onClick={() => {
                      setProjetoAtivo(projeto.id)
                      setOpen(false)
                    }}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-markv-surface-100 ${
                      selected ? "text-markv-primary-accent" : "text-markv-text-700"
                    }`}
                  >
                    <span className="truncate">{projeto.nome}</span>
                    <span className="shrink-0 text-xs text-markv-text-400">
                      {formatPeriodo(projeto)}
                    </span>
                  </button>
                )
              })
            )}
            <div className="border-t border-markv-surface-200" />
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                setDialogOpen(true)
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-markv-primary-accent hover:bg-markv-surface-100"
            >
              <span aria-hidden="true" className="text-base leading-none">
                +
              </span>
              Novo projeto
            </button>
          </div>
        ) : null}
      </div>

      <ProjetoDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(projeto) => {
          setProjetoAtivo(projeto.id)
        }}
      />
    </>
  )
}
